import { useEffect, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { CATEGORIES } from '../data/categories'
import { COLORS_BY_ID, COLOR_WORDS, type ColorId } from '../data/colors'
import { CategoryCardImage, ColorCardImage } from '../components/ColorCardImage'
import { createInitialState, remainingColorsFor, slotForRound } from '../engine/setup'
import type { ColorfulPlayer, ColorfulRound, ColorfulState } from '../engine/types'
import { pickFallbackSubject } from '../api/aiSubject'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useT } from '../../../i18n'

interface Props {
  players: ColorfulPlayer[]
  onExit: () => void
  onFinished: (state: ColorfulState) => void
}

/**
 * Phone Pass Play screen for Colorful.
 *
 * Same engine as ColorfulGameScreen (createInitialState + the round /
 * picks shape) but the picking phase here cycles through *every*
 * player one at a time — humans get a pass-the-phone splash before
 * their pick, AIs auto-fill instantly. The original ColorfulGameScreen
 * was built for solo-vs-AI and assumed `me = players[0]` so it could
 * never drive multiple humans. This screen owns its own picking
 * sequence and adds the splash UI between turns.
 */
export function ColorfulPassPlayGameScreen({ players, onExit, onFinished }: Props) {
  const t = useT()
  const [state, setState] = useState<ColorfulState>(() => createInitialState(players))
  // The local human taps "I'm ready" before each of their turns so the
  // phone can be passed without anyone peeking. We remember the key of
  // the turn they last acknowledged; the splash shows whenever the
  // current turn's key hasn't been acknowledged yet. Reset to null each
  // time a fresh turn begins (card dealt, subject locked, round advance).
  const [ackedKey, setAckedKey] = useState<string | null>(null)
  const [subjectDraft, setSubjectDraft] = useState('')

  const roundIdx = state.currentRound
  const roundNumber = roundIdx + 1
  const picker = state.players[state.picker]
  const round = state.rounds[roundIdx]
  // Whose turn it is to pick a colour is *derived* from how many players
  // have already picked this round, not tracked as a separate counter. A
  // standalone index could be — and was — double-advanced by a racing
  // timer, silently skipping a player's turn every other round. Deriving
  // it makes that impossible.
  //
  // The order starts with the round's PICKER — after locking the subject
  // they're already holding the phone, so they colour first and the phone
  // then travels once around the table instead of jumping back to seat 1.
  const pickOrder = [
    ...state.players.slice(state.picker),
    ...state.players.slice(0, state.picker),
  ]
  const pickCount = round ? Object.keys(round.picks).length : 0
  const currentPickPlayer = state.phase === 'picking' ? pickOrder[pickCount] : undefined
  const pickerKey = `picker:${roundIdx}`
  const pickKey = `pick:${roundIdx}:${pickCount}`

  // Record one colour pick. Fully functional + guarded so a duplicate
  // call (e.g. a leftover timer) can neither write twice nor skip a seat.
  function recordPick(playerId: string, color: ColorId) {
    setState(s => {
      const rounds = s.rounds.slice()
      const cur = rounds[s.currentRound]
      if (!cur || cur.picks[playerId]) return s
      rounds[s.currentRound] = { ...cur, picks: { ...cur.picks, [playerId]: color } }
      return { ...s, rounds }
    })
  }

  // Lock in this round's subject and move to colour-picking. Guarded on
  // phase so a stray double-fire can't push a second round.
  function commitRound(subject: string, pickerKeepsPhone: boolean) {
    setState(s => {
      if (s.phase !== 'picker_draw' || !s.pendingCard) return s
      const newRound: ColorfulRound = {
        categoryId: s.pendingCard.id,
        promptSlot: slotForRound(s.currentRound),
        pickerId: s.players[s.picker].id,
        subject,
        picks: {},
      }
      return { ...s, rounds: [...s.rounds, newRound], pendingCard: null, phase: 'picking' }
    })
    // The picker colours first. A human picker who just typed the subject
    // is already holding the phone — pre-acknowledge their turn so they
    // go straight to their colour grid, no redundant pass splash.
    setAckedKey(pickerKeepsPhone ? `pick:${roundIdx}:0` : null)
  }

  // ── Side effects ────────────────────────────────────────────────────
  // Kept in useEffect (not scheduled during render) so each fires exactly
  // once per state transition and is cleaned up if state moves on first.

  // Deal the next category card at the start of a round.
  useEffect(() => {
    if (state.phase !== 'round_intro') return
    setState(s => {
      if (s.phase !== 'round_intro' || s.deck.length === 0) return s
      return { ...s, deck: s.deck.slice(1), pendingCard: s.deck[0], redrew: false, phase: 'picker_draw' }
    })
  }, [state.phase])

  // AI picker chooses a specific subject from the curated fallback
  // dictionary (offline — no edge function in pass play).
  useEffect(() => {
    if (state.phase !== 'picker_draw' || !state.pendingCard || !picker.isAi) return
    const prompt = state.pendingCard.prompts[slotForRound(roundIdx)]
    const subject = pickFallbackSubject(`${picker.id}|r${roundIdx}`, prompt)
    const t = setTimeout(() => commitRound(subject, false), 600)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.pendingCard?.id, picker.id, roundIdx])

  // AI auto-picks a colour when it's their turn (picker-first order).
  useEffect(() => {
    if (state.phase !== 'picking' || !round) return
    const order = [
      ...state.players.slice(state.picker),
      ...state.players.slice(0, state.picker),
    ]
    const cp = order[pickCount]
    if (!cp || !cp.isAi || round.picks[cp.id]) return
    const t = setTimeout(() => {
      const hand = remainingColorsFor(state, cp.id)
      if (hand.length === 0) return
      const color = hand[Math.floor(Math.random() * hand.length)]
      recordPick(cp.id, color)
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, roundIdx, pickCount])

  // Once everyone has picked, advance to the next round (or results).
  useEffect(() => {
    if (state.phase !== 'picking' || pickCount < state.players.length) return
    setState(s => {
      if (s.phase !== 'picking') return s
      const cur = s.rounds[s.currentRound]
      if (!cur || Object.keys(cur.picks).length < s.players.length) return s
      const isLast = s.currentRound + 1 >= s.maxRounds
      if (isLast) return { ...s, phase: 'results' }
      return {
        ...s,
        currentRound: s.currentRound + 1,
        picker: (s.picker + 1) % s.players.length,
        phase: 'round_intro',
      }
    })
    setAckedKey(null)
  }, [state.phase, roundIdx, pickCount])

  // Hand the final state up to the wrapper for scoring.
  useEffect(() => {
    if (state.phase === 'results') onFinished(state)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase])

  // ── Setup screen ──────────────────────────────────────────────────────
  if (state.phase === 'setup') {
    return (
      <Shell title="Colorful" subtitle={t('Setup')}>
        <div className="text-center space-y-4">
          <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest">{t("Tonight you won't be needing")}</p>
          <h2 className="font-display text-2xl font-bold text-brand-text">{t('These five colors')}</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {state.discardedColors.map(id => (
              <div key={id} className="opacity-50">
                <ColorCardImage color={id} size="lg" />
              </div>
            ))}
          </div>
          <p className="text-sm text-brand-muted max-w-xs mx-auto">
            {t('The {n} remaining colors are shared by every player.', { n: state.availableColors.length })}
          </p>
        </div>
        <FloatingPrimary
          label={t('Ready, deal round 1')}
          onTap={() => setState(s => ({ ...s, phase: 'round_intro' }))}
        />
      </Shell>
    )
  }

  // ── round_intro / picker_draw — pass to picker, picker types subject ───
  // The card is dealt by the round_intro effect above.
  if (state.phase === 'round_intro') {
    return (
      <Shell title="Colorful" subtitle={t('Round {roundNumber}', { roundNumber })}>
        <p className="text-brand-muted">{t('Dealing card…')}</p>
      </Shell>
    )
  }

  if (state.phase === 'picker_draw' && state.pendingCard) {
    if (!picker.isAi && ackedKey !== pickerKey) {
      return (
        <PassSplash
          playerName={picker.name}
          instruction={t("You're picking the subject this round.")}
          onReady={() => setAckedKey(pickerKey)}
        />
      )
    }
    const slot = slotForRound(roundIdx)
    const prompt = state.pendingCard.prompts[slot]
    // The real category card, rotated landscape (same presentation as the
    // online room), with the round's prompt called out below it.
    const cardHeader = (
      <div className="flex flex-col items-center gap-3">
        <CategoryCardImage
          src={state.pendingCard.cardFront}
          alt={t('{theme} category card', { theme: state.pendingCard.theme })}
          size="xl"
        />
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-widest text-brand-muted">
            {t('Round prompt #{n}', { n: slot === 3 ? '4-5' : slot + 1 })}
          </p>
          <p className="font-display text-xl font-bold text-brand-text uppercase tracking-wide">
            {prompt}
          </p>
        </div>
      </div>
    )
    if (picker.isAi) {
      // The AI picker's subject is chosen by the picker_draw effect above.
      return (
        <div className="screen">
          <BackgroundTitle text="Colorful" color="#74705F" />
          <PageHeader
            title={t('Round {roundNumber}', { roundNumber })}
            subtitle={t('{name} · picking a subject', { name: picker.name })}
            onBack={onExit}
          />
          <div className="flex-1 flex flex-col px-5 py-6 gap-5 overflow-y-auto items-center justify-center relative z-10">
            {cardHeader}
            <div className="text-center space-y-1">
              <p className="text-4xl">{picker.avatarEmoji ?? '🤖'}</p>
              <p className="font-display text-2xl font-bold text-brand-text">{picker.name}</p>
              <p className="text-sm text-brand-muted">{t('picking something in this category…')}</p>
            </div>
          </div>
        </div>
      )
    }
    // Human picker UI
    const trimmed = subjectDraft.trim()
    const hasColorWord = trimmed && COLOR_WORDS.some(w => new RegExp(`\\b${w}\\b`, 'i').test(trimmed))
    // Reject the bare category — "food" isn't a valid subject for the
    // category "food". Strip a leading article so "a food" trips too.
    const normSubject = trimmed.toLowerCase().replace(/^(a |an |the )/, '')
    const normPrompt = prompt.toLowerCase().replace(/^(a |an |the )/, '')
    const matchesCategory = trimmed.length > 0 && normSubject === normPrompt
    const canLock = trimmed.length >= 2 && !hasColorWord && !matchesCategory
    return (
      <div className="screen">
        <BackgroundTitle text="Colorful" color="#74705F" />
        <PageHeader
          title={t('Round {roundNumber}', { roundNumber })}
          subtitle={t('{name} · pick a subject', { name: picker.name })}
          onBack={onExit}
        />
        <div className="flex-1 flex flex-col px-5 py-5 gap-4 overflow-y-auto pb-32 relative z-10">
          {cardHeader}
          <p className="text-sm text-brand-muted">{t('Type one specific thing in this category for everyone to color. No color words allowed.')}</p>
          <input
            type="text"
            value={subjectDraft}
            onChange={e => setSubjectDraft(e.target.value.slice(0, 60))}
            placeholder={t('e.g. {example}', { example: exampleSubject(prompt) })}
            autoFocus
            maxLength={60}
            className="w-full px-4 py-3 rounded-2xl border border-brand-border
                       bg-white text-brand-text placeholder:text-brand-muted/60
                       font-display text-lg font-bold
                       focus:outline-hidden focus:border-brand-primary focus:ring-2
                       focus:ring-brand-primary/20"
          />
          {hasColorWord && (
            <p className="text-xs text-brand-danger">{t('No color words allowed. Try something else.')}</p>
          )}
          {matchesCategory && !hasColorWord && (
            <p className="text-xs text-brand-danger">{t("That's the category itself. Pick something specific inside it.")}</p>
          )}
        </div>
        <FloatingPrimary
          label={canLock ? t('Lock subject (you pick first)') : t('Type a subject')}
          disabled={!canLock}
          onTap={() => { commitRound(trimmed, true); setSubjectDraft('') }}
        />
      </div>
    )
  }

  // ── picking — cycle through every player ──────────────────────────────
  if (state.phase === 'picking' && round) {
    // Brief transient after the last pick, before the advance effect runs.
    if (!currentPickPlayer) {
      return (
        <Shell title="Colorful" subtitle={t('Round {roundNumber}', { roundNumber })}>
          <p className="text-brand-muted">{t('Next round…')}</p>
        </Shell>
      )
    }
    if (!currentPickPlayer.isAi && ackedKey !== pickKey) {
      return (
        <PassSplash
          playerName={currentPickPlayer.name}
          instruction={t('Pick the color you think best matches "{subject}"', { subject: round.subject })}
          onReady={() => setAckedKey(pickKey)}
        />
      )
    }
    if (currentPickPlayer.isAi) {
      return (
        <Shell title="Colorful" subtitle={t('Round {roundNumber} · {subject}', { roundNumber, subject: round.subject })}>
          <div className="text-center space-y-3">
            <p className="text-4xl">{currentPickPlayer.avatarEmoji ?? '🤖'}</p>
            <p className="font-display text-2xl font-bold text-brand-text">{currentPickPlayer.name}</p>
            <p className="text-sm text-brand-muted">{t('picking a color…')}</p>
          </div>
        </Shell>
      )
    }
    // Human picking UI
    const hand = currentPickPlayer ? remainingColorsFor(state, currentPickPlayer.id) : []
    const roundCard = CATEGORIES.find(c => c.id === round.categoryId)
    const roundPrompt = roundCard?.prompts[round.promptSlot] ?? ''
    const pickerName = state.players.find(p => p.id === round.pickerId)?.name ?? ''
    return (
      <div className="screen">
        <BackgroundTitle text="Colorful" color="#74705F" />
        <PageHeader
          title={t("{name}'s pick", { name: currentPickPlayer?.name })}
          subtitle={t('Round {roundNumber} · {theme}', { roundNumber, theme: roundCard?.theme ?? '' })}
          onBack={onExit}
        />
        <div className="px-5 py-5 space-y-4">
          <div className="card p-4 bg-game-colorful-light border border-game-colorful/40">
            <div className="flex items-center gap-3">
              {roundCard && (
                <CategoryCardImage
                  src={roundCard.cardFront}
                  alt={t('{theme} category card', { theme: roundCard.theme })}
                  size="sm"
                  className="shrink-0"
                />
              )}
              <div className="min-w-0">
                <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest">
                  {roundPrompt}{pickerName ? t(' · picked by {name}', { name: pickerName }) : ''}
                </p>
                <p className="font-display text-2xl font-bold text-brand-text">{round.subject}</p>
              </div>
            </div>
            <p className="text-xs text-brand-muted mt-2">{t('Pick the color that best matches it.')}</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {hand.map(id => (
              <button
                key={id}
                onClick={() => recordPick(currentPickPlayer.id, id)}
                aria-label={COLORS_BY_ID[id].name}
                className="active:scale-95 transition-transform"
              >
                <ColorCardImage color={id} size="lg" />
              </button>
            ))}
          </div>
          <p className="text-xs text-brand-muted text-center">
            {hand.length === 1
              ? t('1 color left in your hand')
              : t('{n} colors left in your hand', { n: hand.length })}
          </p>
        </div>
      </div>
    )
  }

  // ── results — hand off (onFinished fires from the results effect) ──────
  if (state.phase === 'results') {
    return (
      <Shell title="Colorful" subtitle={t('Tallying…')}>
        <p className="text-brand-muted">{t('Computing scores…')}</p>
      </Shell>
    )
  }

  return (
    <Shell title="Colorful" subtitle="…">
      <p className="text-brand-muted">{t('Unexpected state.')}</p>
    </Shell>
  )
}

// ── Bits ──────────────────────────────────────────────────────────────

function Shell({ title, subtitle, children }: {
  title: string; subtitle: string; children: React.ReactNode
}) {
  return (
    <div className="screen">
      <BackgroundTitle text="Colorful" color="#74705F" />
      <PageHeader title={title} subtitle={subtitle} />
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-6 relative z-10">
        {children}
      </div>
    </div>
  )
}

function PassSplash({ playerName, instruction, onReady }: {
  playerName: string; instruction: string; onReady: () => void
}) {
  const t = useT()
  return (
    <div className="screen items-center justify-center bg-game-colorful-light">
      <BackgroundTitle text="Colorful" color="#74705F" />
      <div className="text-center max-w-sm px-6 space-y-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-text/60">{t('Pass the phone to')}</p>
        <h1 className="font-display text-5xl font-black text-brand-text">{playerName}</h1>
        <p className="text-brand-muted">{instruction}</p>
        <button onClick={onReady} className="btn-colorful w-full mt-4">
          {t("I'm {name}, start", { name: playerName })}
        </button>
      </div>
    </div>
  )
}

function FloatingPrimary({ label, disabled, onTap }: {
  label: string; disabled?: boolean; onTap: () => void
}) {
  return (
    <div className="floating-actions">
      <button
        onClick={onTap}
        disabled={disabled}
        className="btn-colorful disabled:opacity-40"
      >
        {label}
      </button>
    </div>
  )
}

function exampleSubject(prompt: string): string {
  const map: Record<string, string> = {
    'a fruit': 'apple',
    'an animal': 'fox',
    'a vegetable': 'carrot',
    'a drink': 'coffee',
    'a vehicle': 'bicycle',
  }
  return map[prompt.toLowerCase()] ?? 'something specific'
}
